import React, { useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  limit,
  onSnapshot  // 🔥 AJOUTER CETTE LIGNE
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app } from "../../firebaseConfig";
import { toast } from "react-toastify";
import PdfSection from "../../components/PdfSection";

const CarrossierDashboard = () => {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  
  // États pour l'aperçu PDF ultra-moderne
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdf, setCurrentPdf] = useState(null);
  
  const db = getFirestore(app);
  const auth = getAuth();

  const fetchDossiers = async () => {
    const user = auth.currentUser;
    if (!user) return console.error("Utilisateur non authentifié");

    try {
      const snapshot = await getDocs(
        query(
          collection(db, "dossiers"),
          where("revendeurEmail", "==", user.email),
          where("destinataire_type", "==", "Carrossier")
        )
      );
      const dossiersFiltres = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      });
      setDossiers(dossiersFiltres);
    } catch (error) {
      console.error("Erreur lors de la récupération des dossiers :", error);
      toast.error("Erreur lors du chargement des dossiers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
  
    const q = query(
      collection(db, "dossiers"),
      where("revendeurEmail", "==", user.email),
      where("destinataire_type", "==", "Carrossier")
    );
  
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dossiersRealtime = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      });
      setDossiers(dossiersRealtime);
      setLoading(false);
    });
  
    return () => unsubscribe();
  }, []);
  

  const formatDate = (date) => {
    if (!date) return 'Non définie';
    let dateObj;
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string' || typeof date === 'number') {
      dateObj = new Date(date);
    } else {
      return 'Non définie';
    }
    if (isNaN(dateObj.getTime())) return 'Non définie';
    return dateObj.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // 🔥 NOUVELLE FONCTION : Génération ID d'unité cohérente avec le backend
  const generateUnitDeclarationId = (product, productIndex, unitIndex) => {
    const baseName = product.name?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'product';
    const productId = product.productId || product.uuid || product.id || `${baseName}_${productIndex}`;
    return `${productId}_unit_${unitIndex + 1}`;
  };

  // 🔧 FONCTION CORRIGÉE : Obtenir URL du PDF joint par le carrossier
  const getDeclarationCEUrl = (dossier, product, productIndex, unitNumber = null) => {
    console.log(`🔍 Recherche PDF joint pour produit ${productIndex}: ${product.name} ${unitNumber ? `(Porte #${unitNumber})` : ''}`);
    
    // 🎯 PRIORITÉ 1 : Chercher dans les fichiers joints (fichiersProduits)
    if (dossier.fichiersProduits && dossier.fichiersProduits[productIndex]) {
      const files = dossier.fichiersProduits[productIndex];
      console.log(`📁 Fichiers trouvés pour produit ${productIndex}:`, files);
      
      // 🔧 NOUVELLE LOGIQUE : Si quantity > 1, distribuer les fichiers par porte
      if (product.quantity > 1 && files.length > 1 && unitNumber) {
        // Calculer quel fichier correspond à quelle porte
        const fileIndex = (unitNumber - 1) % files.length;
        const pdfFile = files[fileIndex];
        
        if (pdfFile) {
          console.log(`✅ PDF spécifique trouvé pour Porte #${unitNumber}:`, pdfFile.name);
          
          const baseUrl = window.location.hostname === "localhost"
            ? "http://localhost:5000"
            : "https://veryfit.onrender.com";
          
          const fullUrl = `${baseUrl}/api/dossiers/${dossier.id}/attached-file/${productIndex}/${encodeURIComponent(pdfFile.name)}`;
          console.log(`🔗 URL spécifique générée: ${fullUrl}`);
          
          return fullUrl;
        }
      } else {
        // Logique normale : prendre le premier PDF disponible
        const pdfFile = files.find(file => 
          file.name && file.name.toLowerCase().includes('.pdf')
        );
        
        if (pdfFile) {
          console.log(`✅ PDF joint trouvé:`, pdfFile.name);
          
          const baseUrl = window.location.hostname === "localhost"
            ? "http://localhost:5000"
            : "https://veryfit.onrender.com";
          
          const fullUrl = `${baseUrl}/api/dossiers/${dossier.id}/attached-file/${productIndex}/${encodeURIComponent(pdfFile.name)}`;
          console.log(`🔗 URL complète générée: ${fullUrl}`);
          
          return fullUrl;
        }
      }
    }

    // 🎯 PRIORITÉ 2 : Chercher dans attachedFiles (structure alternative)
    if (dossier.attachedFiles && dossier.attachedFiles[productIndex]) {
      const files = dossier.attachedFiles[productIndex];
      const pdfFile = files.find(file => 
        file.name && file.name.toLowerCase().includes('.pdf')
      );
      
      if (pdfFile) {
        console.log(`✅ PDF attaché trouvé:`, pdfFile.name);
        
        const baseUrl = window.location.hostname === "localhost"
          ? "http://localhost:5000"
          : "https://veryfit.onrender.com";
        
        return `${baseUrl}/api/dossiers/${dossier.id}/attached-file/${productIndex}/${encodeURIComponent(pdfFile.name)}`;
      }
    }

    // 🎯 PRIORITÉ 3 : Vérifier documents.declarationCE (structure originale)
    if (dossier.produits && dossier.produits[productIndex]) {
      const produitData = dossier.produits[productIndex];
      
      if (produitData.documents?.declarationCE?.url && produitData.documents.declarationCE.url !== "") {
        console.log(`✅ Déclaration CE trouvée dans documents: ${produitData.documents.declarationCE.url}`);
        return produitData.documents.declarationCE.url;
      }
    }
    
    // 🎯 PRIORITÉ 4 : Fallback : Ancienne structure pour compatibilité
    const declarationCEKey = `declarationCE_${product.productId || product.uuid || productIndex}`;
    const declarationCEUrl = dossier[declarationCEKey];
    
    if (declarationCEUrl) {
      console.log(`✅ Déclaration CE trouvée (ancienne structure): ${declarationCEUrl}`);
      return declarationCEUrl;
    }
    
    console.log(`❌ Aucune déclaration CE trouvée pour produit ${productIndex}: ${product.name} ${unitNumber ? `(Porte #${unitNumber})` : ''}`);
    return null;
  };

  // 🔧 FONCTION AMÉLIORÉE : Afficher le PDF joint OU générer automatiquement
  const handleViewDeclarationCE = async (dossier, product, productIndex, unitNumber = null) => {
    try {
      console.log(`🔍 Ouverture déclaration CE pour produit ${productIndex}${unitNumber ? ` - Porte #${unitNumber}` : ''}: ${product.name}`);
      
      // 1️⃣ Essayer d'abord d'afficher le PDF joint (avec numéro d'unité pour les quantités multiples)
      const pdfUrl = getDeclarationCEUrl(dossier, product, productIndex, unitNumber);
      
      if (pdfUrl) {
        console.log(`🔗 Ouverture du PDF joint: ${pdfUrl}`);
        
        // Vérifier si le fichier existe avant de l'ouvrir
        try {
          const response = await fetch(pdfUrl);
          if (response.ok) {
            const titleSuffix = unitNumber ? ` - Porte #${unitNumber}` : '';
            handlePdfPreview(pdfUrl, `Déclaration de Conformité CE${titleSuffix}`, product.name);
            return;
          } else {
            console.log(`⚠️ Fichier non accessible (${response.status}), génération automatique...`);
          }
        } catch (fetchError) {
          console.log(`⚠️ Erreur d'accès au fichier, génération automatique...`, fetchError);
        }
      }

      // 2️⃣ Si pas de PDF joint ou inaccessible, générer automatiquement
      console.log(`🔄 Génération automatique du PDF pour: ${product.name}${unitNumber ? ` - Porte #${unitNumber}` : ''}`);
      
      const baseUrl = window.location.hostname === "localhost"
        ? "http://localhost:5000"
        : "https://veryfit.onrender.com";
      
      // 🔧 CORRECTION : Utiliser l'ID d'unité pour les quantités multiples si disponible
      let productIdentifier = product.productId || product.uuid || product.id;
      if (unitNumber && product.quantity > 1) {
        // Pour les quantités multiples, utiliser un identifiant unique par unité
        const baseName = product.name?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'product';
        const baseProductId = product.productId || product.uuid || product.id || `${baseName}_${productIndex}`;
        productIdentifier = `${baseProductId}_unit_${unitNumber}`;
      }
      
      const generateUrl = `${baseUrl}/api/dossiers/generate/declaration-ce/${dossier.id}/${productIdentifier}`;
      
      console.log(`🔗 URL génération: ${generateUrl}`);
      
      // Appel à l'API de génération
      const response = await fetch(generateUrl);
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.success && result.url) {
          console.log(`✅ PDF généré avec succès: ${result.url}`);
          
          // Titre avec numéro d'unité si applicable
          const titleSuffix = unitNumber ? ` - Porte #${unitNumber}` : '';
          const title = `Déclaration de Conformité CE (générée)${titleSuffix}`;
          
          // Ouvrir le PDF généré dans le viewer
          handlePdfPreview(result.url, title, product.name);
        } else {
          throw new Error('URL du PDF non disponible dans la réponse');
        }
      } else {
        const errorText = await response.text();
        throw new Error(`Erreur génération PDF: ${response.status} - ${errorText}`);
      }
      
    } catch (error) {
      console.error("Erreur lors de l'affichage de la déclaration CE:", error);
      const errorMessage = unitNumber 
        ? `Erreur lors de l'affichage du document pour la Porte #${unitNumber}: ${error.message}`
        : `Erreur lors de l'affichage du document: ${error.message}`;
      toast.error(errorMessage);
    }
  };

  // 🔥 FONCTION CORRIGÉE : Vérification de toutes les déclarations d'un produit AVEC DEBUG
  const checkAllDeclarations = (dossier, product, productIndex) => {
    const quantity = parseInt(product.quantity) || 1;
    const declarations = [];
    
    console.log(`🔍 Vérification ${quantity} déclarations pour "${product.name}"`);
    console.log(`🎯 PRODUCT DEBUG:`, {
      productIndex,
      productId: product.productId,
      uuid: product.uuid, 
      id: product.id,
      name: product.name
    });
    
    for (let unitIndex = 0; unitIndex < quantity; unitIndex++) {
      const declarationId = generateUnitDeclarationId(product, productIndex, unitIndex);
      console.log(`🔑 GÉNÉRATION ID pour unité ${unitIndex + 1}:`, declarationId);
      
      const statusKey = `${declarationId}_status`;
      const pdfKey = `declarationMontageCarrossierPdf_${declarationId}`;
      const filledKey = `${declarationId}_filled`;
      const montageCompleteKey = `${declarationId}_montage_complete`;
      
      // 🔥 DEBUG: Vérifier TOUTES les clés possibles dans le dossier
      console.log(`🔍 RECHERCHE CLÉS:`, {
        declarationId,
        statusKey,
        montageCompleteKey,
        filledKey,
        statusValue: dossier[statusKey],
        montageValue: dossier[montageCompleteKey],
        filledValue: dossier[filledKey]
      });
      
      const isComplete = dossier[statusKey] === "declaration_montage_complete";
      const hasUrl = !!dossier[pdfKey];
      const isFilled = !!dossier[filledKey];
      const isMontageComplete = !!dossier[montageCompleteKey];
      
      console.log(`📋 Unité ${unitIndex + 1}: ID=${declarationId}, Complete=${isComplete}, PDF=${hasUrl}, Filled=${isFilled}, MontageComplete=${isMontageComplete}`);
      
      // 🔥 DEBUG: Afficher toutes les clés du dossier qui correspondent à ce produit
      const relatedKeys = Object.keys(dossier).filter(key => 
        key.includes(product.productId || product.uuid || product.id || product.name?.toLowerCase().replace(/[^a-z0-9]/g, ''))
      );
      if (relatedKeys.length > 0) {
        console.log(`🔎 CLÉS LIÉES TROUVÉES pour ${declarationId}:`, relatedKeys);
        relatedKeys.forEach(key => {
          console.log(`  - ${key}: ${dossier[key]}`);
        });
      }
      
      declarations.push({
        unitNumber: unitIndex + 1,
        unitIndex,
        declarationId,
        isComplete,
        hasUrl,
        isFilled,
        isMontageComplete,
        pdfUrl: dossier[pdfKey] || null,
        statusKey,
        pdfKey,
        filledKey,
        montageCompleteKey
      });
    }
    
    const completedCount = declarations.filter(d => d.isComplete && d.hasUrl).length;
    const filledCount = declarations.filter(d => d.isFilled).length;
    const montageCompleteCount = declarations.filter(d => d.isMontageComplete).length;
    const isAllComplete = completedCount === quantity;
    const hasPartial = completedCount > 0 && completedCount < quantity;
    
    console.log(`📊 Résultat: ${completedCount}/${quantity} déclarations complètes, ${filledCount}/${quantity} formulaires remplis, ${montageCompleteCount}/${quantity} contrôles montage terminés`);
    
    return {
      declarations,
      quantity,
      completedCount,
      filledCount,
      montageCompleteCount,
      isAllComplete,
      hasPartial,
      completionRate: Math.round((completedCount / quantity) * 100),
      filledRate: Math.round((filledCount / quantity) * 100),
      montageCompletionRate: Math.round((montageCompleteCount / quantity) * 100)
    };
  };

  // 🔥 NOUVELLES STATS avec déclarations multiples et contrôles montage
  const calculateStats = () => {
    let totalDeclarations = 0;
    let completedDeclarations = 0;
    let montageCompleteDeclarations = 0; // 🔥 NOUVEAU
    let dossiersWithMissingDeclarations = 0;
    let dossiersWithMissingMontage = 0; // 🔥 NOUVEAU
    
    dossiers.forEach(dossier => {
      let dossierHasMissing = false;
      let dossierHasMissingMontage = false; // 🔥 NOUVEAU
      
      if (Array.isArray(dossier.produits)) {
        dossier.produits.forEach((product, index) => {
          const declarationStatus = checkAllDeclarations(dossier, product, index);
          totalDeclarations += declarationStatus.quantity;
          completedDeclarations += declarationStatus.completedCount;
          montageCompleteDeclarations += declarationStatus.montageCompleteCount; // 🔥 NOUVEAU
          
          if (!declarationStatus.isAllComplete) {
            dossierHasMissing = true;
          }
          
          if (declarationStatus.montageCompleteCount < declarationStatus.quantity) { // 🔥 NOUVEAU
            dossierHasMissingMontage = true;
          }
        });
      }
      
      if (dossierHasMissing) {
        dossiersWithMissingDeclarations++;
      }
      
      if (dossierHasMissingMontage) { // 🔥 NOUVEAU
        dossiersWithMissingMontage++;
      }
    });
    
    return {
      total: dossiers.length,
      pending: dossiers.filter(d => !d.produits?.every(p => p.filled)).length,
      declarations: dossiersWithMissingDeclarations,
      montageIncomplete: dossiersWithMissingMontage, // 🔥 NOUVEAU
      completed: dossiers.filter(d => d.produits?.every(p => p.filled)).length,
      totalDeclarations,
      completedDeclarations,
      montageCompleteDeclarations, // 🔥 NOUVEAU
      declarationRate: totalDeclarations > 0 ? Math.round((completedDeclarations / totalDeclarations) * 100) : 0,
      montageCompletionRate: totalDeclarations > 0 ? Math.round((montageCompleteDeclarations / totalDeclarations) * 100) : 0 // 🔥 NOUVEAU
    };
  };

  const stats = calculateStats();

  // Filtrage et tri
  const filteredDossiers = dossiers.filter(dossier => {
    const matchesSearch = dossier.orderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dossier.produits?.some(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (filterStatus === "all") return matchesSearch;
    if (filterStatus === "pending") return matchesSearch && !dossier.produits?.every(p => p.filled);
    if (filterStatus === "completed") return matchesSearch && dossier.produits?.every(p => p.filled);
    
    if (filterStatus === "declarations") {
      const hasIncompleteDeclarations = dossier.produits?.some((product, index) => {
        const declarationStatus = checkAllDeclarations(dossier, product, index);
        return !declarationStatus.isAllComplete;
      });
      return matchesSearch && hasIncompleteDeclarations;
    }
    
    return matchesSearch;
  }).sort((a, b) => {
    if (sortBy === "recent") return new Date(b.createdAt) - new Date(a.createdAt);
    if (sortBy === "name") return a.orderName.localeCompare(b.orderName);
    if (sortBy === "status") {
      const aComplete = a.produits?.every(p => p.filled) ? 1 : 0;
      const bComplete = b.produits?.every(p => p.filled) ? 1 : 0;
      return bComplete - aComplete;
    }
    return 0;
  });

  // Gestionnaires d'événements
  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce dossier ?")) return;
    try {
      await deleteDoc(doc(db, "dossiers", id));
      toast.success("Dossier supprimé avec succès");
      fetchDossiers();
    } catch (err) {
      console.error("Erreur lors de la suppression du dossier :", err);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Gestionnaire pour l'aperçu PDF
  const handlePdfPreview = (pdfUrl, title, productName) => {
    setCurrentPdf({ url: pdfUrl, title, productName });
    setShowPdfViewer(true);
  };

  // Composant PDF Viewer ultra-moderne
  const ModernPdfViewer = () => {
    if (!showPdfViewer || !currentPdf) return null;

    return (
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm">
        <div className="h-full flex flex-col">
          
          {/* Barre de navigation supérieure */}
          <div className="bg-gray-900/95 backdrop-blur-md border-b border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowPdfViewer(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors group"
                >
                  <svg className="w-5 h-5 text-gray-300 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <div className="text-white">
                  <h3 className="font-semibold text-lg">{currentPdf.title}</h3>
                  <p className="text-gray-300 text-sm">{currentPdf.productName}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <a
                  href={currentPdf.url}
                  download
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Télécharger
                </a>
                
                <a
                  href={currentPdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Ouvrir
                </a>
              </div>
            </div>
          </div>
          
          {/* Contenu PDF */}
          <div className="flex-1 relative">
            <iframe
              src={`${currentPdf.url}#view=FitH&navpanes=0&toolbar=1`}
              className="w-full h-full border-0 bg-white"
              title={currentPdf.title}
            />
            
            {/* Overlay de contrôles en bas */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
              <div className="bg-gray-900/90 backdrop-blur-md rounded-xl px-4 py-2 flex items-center space-x-3">
                <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-300 hover:text-white">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </button>
                <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-300 hover:text-white">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                  </svg>
                </button>
                <div className="w-px h-6 bg-gray-600"></div>
                <span className="text-gray-300 text-sm font-medium">ESC pour fermer</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Hook pour fermer avec ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showPdfViewer) {
        setShowPdfViewer(false);
      }
    };
    
    if (showPdfViewer) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showPdfViewer]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-orange-200 rounded-full animate-spin"></div>
            <div className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Chargement en cours</h3>
            <p className="text-gray-600">Récupération de vos dossiers...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      
      {/* Header - BLOC NORMAL SANS POSITIONNEMENT */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          
          {/* Titre et stats - RESPONSIVE */}
          <div className="flex flex-col space-y-6 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                🔧 Gestion des dossiers CE - Carrossier
              </h1>
              <p className="text-gray-600 text-sm sm:text-base">
                Suivez vos installations et déclarations de montage
              </p>
            </div>
            
            {/* Stats rapides - GRID RESPONSIVE - 🔥 AJOUT STAT MONTAGE */}
           {/* Stats rapides - GRID RESPONSIVE - 🔥 TOUTES LES STATS */}
<div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
  <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100">
    <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total}</div>
    <div className="text-xs sm:text-sm text-gray-600">Total dossiers</div>
  </div>
  <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100">
    <div className="text-xl sm:text-2xl font-bold text-orange-600">{stats.pending}</div>
    <div className="text-xs sm:text-sm text-gray-600">En cours</div>
  </div>
  <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100">
    <div className="text-xl sm:text-2xl font-bold text-red-600">{stats.declarations}</div>
    <div className="text-xs sm:text-sm text-gray-600">Décl. manquantes</div>
  </div>
  <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100">
    <div className="text-xl sm:text-2xl font-bold text-blue-600">{stats.montageIncomplete}</div>
    <div className="text-xs sm:text-sm text-gray-600">Montage incomplet</div>
  </div>
  <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100">
    <div className="text-xl sm:text-2xl font-bold text-green-600">{stats.completed}</div>
    <div className="text-xs sm:text-sm text-gray-600">Terminés</div>
  </div>
</div>
            
            {/* Stats détaillées des déclarations - RESPONSIVE - 🔥 NOUVEAU DESIGN AVEC MONTAGE */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-blue-700">Déclarations de montage</div>
                  <div className="text-xl sm:text-2xl font-bold text-blue-900">
                    {stats.completedDeclarations}/{stats.totalDeclarations}
                  </div>
                  <div className="text-sm text-blue-600">Taux: {stats.declarationRate}%</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-indigo-700">Contrôles montage</div>
                  <div className="text-xl sm:text-2xl font-bold text-indigo-900">
                    {stats.montageCompleteDeclarations}/{stats.totalDeclarations}
                  </div>
                  <div className="text-sm text-indigo-600">Taux: {stats.montageCompletionRate}%</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Barre de recherche et filtres - RESPONSIVE */}
          <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-gray-500 text-sm sm:text-base"
                placeholder="Rechercher un dossier ou produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex gap-3">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 sm:px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En cours</option>
                <option value="completed">Terminés</option>
                <option value="declarations">Déclarations manquantes</option>
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 sm:px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
              >
                <option value="recent">Plus récents</option>
                <option value="name">Nom A-Z</option>
                <option value="status">Statut</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal - BLOC NORMAL */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        
        {filteredDossiers.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="mx-auto w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? "Aucun résultat trouvé" : "Aucun dossier disponible"}
            </h3>
            <p className="text-gray-600 max-w-md mx-auto text-sm sm:text-base">
              {searchTerm 
                ? "Essayez de modifier vos critères de recherche"
                : "Vos dossiers CE apparaîtront ici une fois qu'ils vous seront assignés"
              }
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm sm:text-base"
              >
                Effacer la recherche
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {filteredDossiers.map((dossier) => (
              <div
                key={dossier.id}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
              >
                
                {/* Header du dossier - RESPONSIVE */}
                <div className="relative bg-gradient-to-r from-orange-50 via-red-50 to-pink-50 p-4 sm:p-6 border-b border-gray-100">
                  <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                          {dossier.orderName}
                        </h2>
                        {dossier.produits?.some((product, index) => {
                          const declarationStatus = checkAllDeclarations(dossier, product, index);
                          return !declarationStatus.isAllComplete;
                        }) && (
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                          <span>Créé le {formatDate(dossier.createdAt)}</span>
                        </div>
                        {dossier.deliveryDate && (
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                            <span>Livraison : {dossier.deliveryDate}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <span
                        className={`px-3 py-1.5 text-xs sm:text-sm rounded-full font-medium ${
                          dossier.status === "en_attente_remplissage"
                            ? "bg-orange-100 text-orange-700 border border-orange-200"
                            : dossier.status === "validé"
                            ? "bg-green-100 text-green-700 border border-green-200"
                            : "bg-gray-100 text-gray-700 border border-gray-200"
                        }`}
                      >
                        {dossier.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Section produits - RESPONSIVE */}
                <div className="p-4 sm:p-6">
                  {Array.isArray(dossier.produits) && dossier.produits.length > 0 ? (
                    <div className="space-y-4 sm:space-y-6">
                      {dossier.produits.map((product, i) => {
                        const declarationStatus = checkAllDeclarations(dossier, product, i);
                        const { declarations, quantity, completedCount, isAllComplete, hasPartial, completionRate } = declarationStatus;
                        const declarationCEUrl = getDeclarationCEUrl(dossier, product, i);
                        
                        return (
                          <div
                            key={`${product.name}_${i}`}
                            className="bg-gradient-to-r from-gray-50 to-gray-50/50 rounded-xl p-4 sm:p-5 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-200"
                          >
                            <div className="flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:gap-6">
                              
                              {/* Informations produit - RESPONSIVE */}
                              <div className="flex-1 space-y-3">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                  <div className="flex-1">
                                    <h3 className="font-semibold text-base sm:text-lg text-gray-900 mb-1">
                                      🛠️ {product.name}
                                    </h3>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs sm:text-sm text-gray-600">
                                      <span>Quantité : {quantity}</span>
                                      <span className="hidden sm:inline text-gray-400">•</span>
                                      <span className={completionRate === 100 ? 'text-green-600 font-medium' : 'text-orange-600 font-medium'}>
                                        {completedCount}/{quantity} déclarations complètes ({completionRate}%)
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <div className="flex sm:hidden lg:flex">
                                    <div className={`w-3 h-3 rounded-full ${
                                      isAllComplete ? 'bg-green-500' : hasPartial ? 'bg-orange-500' : 'bg-red-500'
                                    }`}></div>
                                  </div>
                                </div>
                                
                                {/* 🔍 AFFICHAGE DEBUG : Voir les fichiers disponibles */}
                                {(dossier.fichiersProduits?.[i] || dossier.attachedFiles?.[i]) && (
                                  <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="text-xs text-blue-600 font-medium mb-1">
                                      📎 Fichiers joints disponibles:
                                    </div>
                                    <div className="text-xs text-blue-700">
                                      {(dossier.fichiersProduits?.[i] || dossier.attachedFiles?.[i])
                                        ?.map(f => f.name).join(', ')}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Badges de statut - RESPONSIVE */}
                                <div className="flex flex-wrap gap-2">
                                  {isAllComplete ? (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                      Toutes déclarations complètes
                                    </span>
                                  ) : hasPartial ? (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                      </svg>
                                      Partielles ({completedCount}/{quantity})
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                      </svg>
                                      Contrôles à effectuer
                                    </span>
                                  )}
                                  
                                  {declarationCEUrl && (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" clipRule="evenodd" />
                                        <path fillRule="evenodd" d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" clipRule="evenodd" />
                                      </svg>
                                      📧 Déclaration CE disponible
                                    </span>
                                  )}
                                </div>

                                {/* Liste détaillée des unités - RESPONSIVE */}
                                {quantity > 1 && (
                                  <div className="mt-4 space-y-2">
                                    <h4 className="text-sm font-medium text-gray-700">Déclarations par porte :</h4>
                                    <div className="space-y-2">
                                      {declarations.map((declaration) => (
                                        <div key={declaration.declarationId} className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white p-3 rounded-lg border border-gray-200 gap-2">
                                          <div className="flex items-center gap-3">
                                            <div className={`w-2 h-2 rounded-full ${
                                              declaration.isComplete && declaration.hasUrl ? 'bg-green-500' : declaration.isMontageComplete ? 'bg-blue-500' : declaration.isFilled ? 'bg-orange-500' : 'bg-red-500'
                                            }`}></div>
                                            <span className="text-sm font-medium text-gray-900">
                                              🚪 Porte #{declaration.unitNumber}
                                            </span>
                                            <span className="text-xs text-gray-500 hidden sm:inline">
                                              (ID: {declaration.declarationId})
                                            </span>
                                          </div>
                                          
                                          <div className="flex items-center gap-2">
                                            {declaration.isComplete && declaration.hasUrl ? (
                                              <button
                                                onClick={() => handlePdfPreview(declaration.pdfUrl, `Déclaration Porte #${declaration.unitNumber}`, product.name)}
                                                className="text-xs bg-green-100 hover:bg-green-200 text-green-700 px-2 py-1 rounded transition-colors"
                                              >
                                                📄 Voir PDF
                                              </button>
                                            ) : declaration.isMontageComplete ? (
                                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                                ⏳ Montage terminé - Déclaration à générer
                                              </span>
                                            ) : declaration.isFilled ? (
                                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                                                📝 Montage à finaliser
                                              </span>
                                            ) : (
                                              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                                ❌ À effectuer
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Panel d'actions - RESPONSIVE */}
                              <div className="w-full lg:w-80 xl:w-96 space-y-3">
                                
                                {/* Section pour chaque porte - RESPONSIVE */}
                                <div className="space-y-3">
                                  <div className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
                                    🚪 Actions par porte ({quantity} porte{quantity > 1 ? 's' : ''})
                                  </div>
                                  
                                  {declarations.map((declaration) => (
                                    <div key={declaration.declarationId} className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 space-y-3">
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                        <h5 className="font-medium text-gray-900 flex items-center gap-2">
                                          🚪 Porte #{declaration.unitNumber}
                                          <div className={`w-2 h-2 rounded-full ${
                                            declaration.isComplete && declaration.hasUrl ? 'bg-green-500' : declaration.isMontageComplete ? 'bg-blue-500' : 'bg-red-500'
                                          }`}></div>
                                        </h5>
                                        <span className="text-xs text-gray-500">
                                          ID: {declaration.declarationId}
                                        </span>
                                      </div>
                                      
                                      {/* Étape 1 : Formulaire de montage et mise en service - RESPONSIVE - 🔥 NOUVEAU DESIGN */}
                                      <a
href={`/carrossier/orders/${dossier.id}/produits/${product.productId || product.uuid || product.id || encodeURIComponent(product.name?.toLowerCase() || "")}?unit=${declaration.unitNumber}`}
className={`block w-full text-center px-3 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] ${
                                          declaration.isMontageComplete
                                            ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md hover:shadow-lg"
                                            : declaration.isFilled
                                            ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md hover:shadow-lg"
                                            : "bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-md hover:shadow-lg"
                                        }`}
                                      >
                                        {declaration.isMontageComplete ? (
                                          <div className="flex items-center justify-center gap-2">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            <span className="hidden sm:inline">✅ Contrôle montage terminé</span>
                                            <span className="sm:hidden">✅ Terminé</span>
                                          </div>
                                        ) : declaration.isFilled ? (
                                          <div className="flex items-center justify-center gap-2">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                                            </svg>
                                            <span className="hidden sm:inline">🔄 Finaliser contrôle montage</span>
                                            <span className="sm:hidden">🔄 Finaliser</span>
                                          </div>
                                        ) : (
                                          <div className="flex items-center justify-center gap-2">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                                            </svg>
                                            <span className="hidden sm:inline">📝 Remplir Contrôle montage et mise en service</span>
                                            <span className="sm:hidden">📝 Montage</span>
                                          </div>
                                        )}
                                      </a>
                                      
                                      {/* Étape 2 : Déclaration de montage finale - RESPONSIVE */}
                                      {declaration.isComplete && declaration.hasUrl ? (
                                        <button
                                          onClick={() => {
                                            if (declaration.pdfUrl) {
                                              handlePdfPreview(declaration.pdfUrl, `Déclaration Porte #${declaration.unitNumber}`, product.name);
                                            } else {
                                              const generateUrl = `https://veryfit.onrender.com/api/generate/declaration-montage-carrossier/${dossier.id}/${declaration.declarationId}`;
                                              window.open(generateUrl, '_blank');
                                            }
                                          }}
                                          className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-2.5 rounded-lg font-medium text-sm shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                                        >
                                          <div className="flex items-center justify-center gap-2">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
                                            </svg>
                                            <span className="hidden sm:inline">🧾 Voir déclaration de montage</span>
                                            <span className="sm:hidden">🧾 Déclaration</span>
                                          </div>
                                        </button>
                                      ) : (
                                        <a
                                          href={`/carrossier/orders/${dossier.id}/declaration-montage/${declaration.declarationId}`}
                                          className="block w-full text-center bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-3 py-2.5 rounded-lg font-medium text-sm shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                                        >
                                          <div className="flex items-center justify-center gap-2">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                            <span className="hidden sm:inline">✍️ Générer déclaration de montage</span>
                                            <span className="sm:hidden">✍️ Générer</span>
                                          </div>
                                        </a>
                                      )}
                                      
                                      {/* 🚀 BOUTON MODIFIÉ : Télécharger Déclaration CE - PAR PORTE */}
                                      <button
                                        onClick={() => handleViewDeclarationCE(dossier, product, i, declaration.unitNumber)}
                                        className={`w-full px-3 py-2.5 rounded-lg font-medium text-sm shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] ${
                                          declarationCEUrl 
                                            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700'
                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        }`}
                                        disabled={!declarationCEUrl}
                                      >
                                        <div className="flex items-center justify-center gap-2">
                                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" clipRule="evenodd" />
                                            <path fillRule="evenodd" d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" clipRule="evenodd" />
                                          </svg>
                                          <span className="hidden sm:inline">📄 Voir Déclaration CE</span>
                                          <span className="sm:hidden">📄 CE</span>
                                        </div>
                                      </button>
                                      
                                      {/* Status de cette porte - RESPONSIVE - 🔥 NOUVEAU AVEC MONTAGE */}
                                      <div className="text-xs text-center text-gray-500 bg-gray-50 py-2 rounded">
                                        {declaration.isComplete && declaration.hasUrl ? (
                                          <span className="text-green-600 font-medium">✅ Porte complètement traitée</span>
                                        ) : declaration.isMontageComplete ? (
                                          <span className="text-blue-600 font-medium">⏳ Contrôle montage terminé - Déclaration à générer</span>
                                        ) : declaration.isFilled ? (
                                          <span className="text-orange-600 font-medium">📝 Contrôle montage à finaliser</span>
                                        ) : (
                                          <span className="text-red-600 font-medium">❌ Contrôle montage à effectuer</span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-red-50 rounded-xl border border-red-200">
                      <div className="text-red-400 text-4xl mb-3">⚠️</div>
                      <p className="text-red-700 font-medium">
                        Aucun produit associé à ce dossier
                      </p>
                    </div>
                  )}
                </div>

                {/* 🚀 SECTION PDF - RESPONSIVE - Toujours visible */}
                <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                  <PdfSection 
                    dossier={dossier} 
                    onPdfPreview={handlePdfPreview}
                  />
                </div>

                {/* Footer du dossier - RESPONSIVE */}
                <div className="bg-gray-50/50 px-4 sm:px-6 py-4 border-t border-gray-100">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://drive.google.com/drive/folders/1I7T4QiutUZEqOhQlmwCpHJvv_PdWw-QC"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 text-sm transition-colors shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                      </svg>
                      📘 Notice d'instruction
                    </a>
                    {dossier.status !== "validé" && (
                      <button
                        onClick={() => handleDelete(dossier.id)}
                        className="inline-flex items-center justify-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 text-sm transition-colors"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Supprimer
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PDF Viewer ultra-moderne */}
        <ModernPdfViewer />
      </div>
    </div>
  );
};

export default CarrossierDashboard;